import React, { useState, useEffect } from "react";
import { BrandLogo } from "@/components/Brand";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Copy, Check, MapPin, MessageCircle, Activity, Compass, ArrowRight, Users, Heart, Mail, Sparkles } from "lucide-react";

const CREDS = {
    email: "yc@memorylane.app",
    password: "YC-Reviewer-2026",
};

const TOUR = [
    {
        n: "1",
        icon: <Compass strokeWidth={1.6} size={20} />,
        title: "Dashboard — Sarah is overwhelmed",
        body: "After login you'll land on her dashboard. Burnout 7/10 in yellow. Wandering in coral. Stage 4. One — only one — priority for today: take a break this week. Notice the 'What's been working' card already remembers her wins.",
        to: "/dashboard",
    },
    {
        n: "2",
        icon: <MapPin strokeWidth={1.6} size={20} />,
        title: "Roadmap — what to do next",
        body: "The visual FAST 1-7 journey. Sage early, warm sand middle (Sarah is here), dusty lavender late. She can see what's missing from earlier stages — MPOA still incomplete — and what's coming next.",
        to: "/roadmap",
    },
    {
        n: "3",
        icon: <Activity strokeWidth={1.6} size={20} />,
        title: "Behavior — wandering guidance",
        body: "Evidence-backed: 'what's happening in the brain' for wandering at night, what makes it worse, and the specific strategies that helped Sarah on prior nights (door chime + nightlight).",
        to: "/guidance",
    },
    {
        n: "4",
        icon: <MessageCircle strokeWidth={1.6} size={20} />,
        title: "Lane — real support, not a chatbot",
        body: "Ask: 'Sleep problems are happening again tonight. What should I try?' Lane references Sarah's logged wins by name — and meets her where she actually is.",
        to: "/chat",
    },
    {
        n: "5",
        icon: <Users strokeWidth={1.6} size={20} />,
        title: "Family — share the workload",
        body: "Sarah's brother lives in Phoenix and says 'let me know how I can help' but never follows up. The Share My Situation page drafts a one-paragraph update with specific, time-boxed asks she can send him tonight.",
        to: "/family",
    },
];

const SARAH_FACTS = [
    "Caring for Mom alone",
    "FAST 4",
    "Burnout 7/10",
    "Wandering at night",
    "Poor sleep — both of them",
    "Family lives out of state",
    "MPOA not yet completed",
    "Recent medication changes",
];

function CopyButton({ value, label }) {
    const [copied, setCopied] = useState(false);
    return (
        <button
            type="button"
            data-testid={`yc-copy-${label}`}
            onClick={async () => {
                try {
                    await navigator.clipboard.writeText(value);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1600);
                } catch {
                    // no-op
                }
            }}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-dark hover:text-teal bg-teal-soft/60 hover:bg-teal-soft px-2.5 py-1 rounded-2xl transition-colors"
        >
            {copied ? <Check size={14} strokeWidth={2.2} /> : <Copy size={14} strokeWidth={1.8} />}
            {copied ? "Copied" : "Copy"}
        </button>
    );
}

export default function YCReviewer() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [signingIn, setSigningIn] = useState(false);

    // Track that a reviewer opened the page so the founder can see it in /admin.
    useEffect(() => {
        api.post("/yc/track", {
            event_type: "page_view",
            path: "/yc",
            referrer: typeof document !== "undefined" ? document.referrer : "",
        }).catch(() => {});
    }, []);

    const oneClickLogin = async () => {
        setSigningIn(true);
        try {
            await login(CREDS.email, CREDS.password);
            toast.success("Welcome — you're in Sarah's account.");
            // Tiny pause so the toast registers before nav.
            setTimeout(() => navigate("/dashboard"), 400);
        } catch (e) {
            toast.error("Couldn't auto-sign-in. Use the credentials below.");
            setSigningIn(false);
        }
    };

    return (
        <div className="min-h-screen bg-cream" data-testid="yc-reviewer-page">
            {/* Header */}
            <header className="bg-cream/80 backdrop-blur-sm border-b border-[#E2E8F0]/60">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2.5">
                        <BrandLogo className="h-6" />
                    </Link>
                    <Link to="/" className="text-sm font-medium text-[#4C5551] hover:text-teal-dark">
                        ← Back to site
                    </Link>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-16 space-y-12">
                {/* Hero */}
                <section>
                    <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark mb-3">
                        Welcome, Y Combinator reviewer
                    </p>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold tracking-tight leading-[1.05] text-[#12776F]">
                        Meet Sarah.
                    </h1>
                    <p className="mt-4 text-lg md:text-xl text-[#4C5551] leading-relaxed max-w-2xl">
                        She&apos;s the person we built Memory Lane for. We&apos;ve loaded her account
                        with real intake, real behavior logs, and a real Lane conversation —
                        so you can experience the app the way she does. No setup. Just click below.
                    </p>

                    {/* One-click sign-in */}
                    <div className="mt-7 flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={oneClickLogin}
                            disabled={signingIn}
                            data-testid="yc-one-click-login"
                            className="ml-btn-primary text-base disabled:opacity-60 disabled:cursor-wait"
                        >
                            {signingIn ? (
                                <>Signing you in&hellip;</>
                            ) : (
                                <>
                                    <Sparkles strokeWidth={1.8} size={18} />
                                    Sign me in as Sarah
                                    <ArrowRight strokeWidth={1.8} size={18} />
                                </>
                            )}
                        </button>
                        <span className="text-sm text-[#4C5551]">
                            One click. No password to type.
                        </span>
                    </div>
                </section>

                {/* Meet Sarah — emotional persona card */}
                <section
                    data-testid="yc-meet-sarah"
                    className="ml-card p-6 md:p-8 bg-stage_middle/40 !border-stage_middle"
                >
                    <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-start">
                        <div className="md:col-span-4 flex flex-col items-start gap-3">
                            <div className="w-16 h-16 rounded-2xl bg-stage_middle flex items-center justify-center">
                                <Heart strokeWidth={1.6} size={28} className="text-stage_middle-dark" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider font-semibold text-stage_middle-dark">
                                    Caregiver
                                </p>
                                <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                                    Sarah
                                </h2>
                                <p className="mt-1 text-sm md:text-base text-[#4C5551] italic">
                                    Caring for her mom. At home. Mostly alone.
                                </p>
                            </div>
                        </div>
                        <div className="md:col-span-8">
                            <p className="text-xs uppercase tracking-wider font-semibold text-stage_middle-dark mb-3">
                                What she&apos;s carrying right now
                            </p>
                            <ul className="grid sm:grid-cols-2 gap-2.5">
                                {SARAH_FACTS.map((f) => (
                                    <li
                                        key={f}
                                        className="flex items-start gap-2.5 text-sm md:text-base text-[#1F2624]"
                                    >
                                        <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-stage_middle-dark" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-5 text-sm md:text-base text-[#4C5551] leading-relaxed border-t border-stage_middle/60 pt-4">
                                Most apps give Sarah a 40-page PDF. We give her a route — one priority today,
                                a roadmap of what&apos;s coming, and an AI companion that remembers what&apos;s
                                already worked.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Credentials card — secondary surface (for pasting into the YC application form) */}
                <section className="ml-card p-6 md:p-8 bg-white" data-testid="yc-credentials">
                    <div className="flex items-start gap-4">
                        <div className="shrink-0 w-12 h-12 rounded-2xl bg-teal-soft flex items-center justify-center">
                            <Compass strokeWidth={1.6} size={22} className="text-teal-dark" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-xl md:text-2xl font-heading font-medium text-[#12776F]">
                                Need to paste credentials into the YC form?
                            </h2>
                            <p className="mt-1.5 text-sm md:text-base text-[#4C5551]">
                                Here they are — same account as the button above.
                            </p>
                            <dl className="mt-5 grid sm:grid-cols-2 gap-4">
                                <div className="rounded-2xl border border-[#E2E8F0] bg-cream-dark/40 p-4">
                                    <dt className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Email</dt>
                                    <dd className="mt-1 flex items-center justify-between gap-3">
                                        <code className="font-mono text-sm md:text-base text-[#1F2624] break-all" data-testid="yc-email">
                                            {CREDS.email}
                                        </code>
                                        <CopyButton value={CREDS.email} label="email" />
                                    </dd>
                                </div>
                                <div className="rounded-2xl border border-[#E2E8F0] bg-cream-dark/40 p-4">
                                    <dt className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Password</dt>
                                    <dd className="mt-1 flex items-center justify-between gap-3">
                                        <code className="font-mono text-sm md:text-base text-[#1F2624] break-all" data-testid="yc-password">
                                            {CREDS.password}
                                        </code>
                                        <CopyButton value={CREDS.password} label="password" />
                                    </dd>
                                </div>
                            </dl>
                            <div className="mt-6 flex flex-wrap gap-3">
                                <Link
                                    to="/login"
                                    data-testid="yc-login-cta"
                                    className="inline-flex items-center justify-center gap-2 bg-white hover:bg-cream-dark border-2 border-teal text-teal-dark rounded-2xl px-5 py-2.5 font-medium transition-all min-h-[44px] text-sm"
                                >
                                    Or use the regular login page
                                    <ArrowRight strokeWidth={1.8} size={16} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 5-step tour — Sarah's narrative */}
                <section data-testid="yc-tour">
                    <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark mb-2">
                        Suggested 5-minute tour
                    </p>
                    <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                        Walk a day in Sarah&apos;s shoes.
                    </h2>
                    <p className="mt-2 text-base md:text-lg text-[#4C5551] max-w-2xl">
                        Follow these five screens in order. Each one mirrors the moment a real caregiver
                        opens the app — overwhelmed, scared, and looking for the next right step.
                    </p>
                    <ol className="mt-7 space-y-4">
                        {TOUR.map((t) => (
                            <li
                                key={t.n}
                                data-testid={`yc-tour-step-${t.n}`}
                                className="ml-card p-5 md:p-6 bg-white flex items-start gap-4"
                            >
                                <div className="shrink-0 w-10 h-10 rounded-2xl bg-stage_early flex items-center justify-center text-stage_early-dark font-heading font-semibold">
                                    {t.n}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 text-teal-dark">
                                        {t.icon}
                                        <h3 className="font-heading text-lg font-medium text-[#1F2624]">{t.title}</h3>
                                    </div>
                                    <p className="mt-1.5 text-sm md:text-base text-[#4C5551] leading-relaxed">{t.body}</p>
                                    <Link
                                        to={t.to}
                                        className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-teal-dark underline underline-offset-2 hover:text-teal"
                                    >
                                        Open {t.to}
                                        <ArrowRight strokeWidth={1.8} size={14} />
                                    </Link>
                                </div>
                            </li>
                        ))}
                    </ol>
                </section>

                {/* Sarah's family message — concrete output of step 5 */}
                <section data-testid="yc-family-message" className="ml-card p-6 md:p-8 bg-white">
                    <div className="flex items-start gap-3 mb-4">
                        <Mail strokeWidth={1.6} size={22} className="text-teal-dark mt-0.5 shrink-0" />
                        <div>
                            <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark">
                                What Lane drafted for Sarah
                            </p>
                            <h2 className="mt-1 text-xl md:text-2xl font-heading font-medium text-[#12776F]">
                                The message to her brother in Phoenix.
                            </h2>
                            <p className="mt-1.5 text-sm md:text-base text-[#4C5551]">
                                One paragraph. Specific, time-boxed, easy to say yes to.
                                This is the actual output of the Family Support page in the app.
                            </p>
                        </div>
                    </div>

                    <div className="rounded-2xl bg-cream border border-cream-dark p-5 md:p-6 space-y-3.5 text-[#1F2624] leading-relaxed text-sm md:text-base">
                        <p>
                            <span className="font-medium">To:</span> Mark<br />
                            <span className="font-medium">Subject:</span> Three specific ways you can help with Mom this month
                        </p>
                        <div className="border-t border-cream-dark pt-3.5 space-y-3">
                            <p>Hi Mark — quick update on Mom, and three asks.</p>
                            <p>
                                She&apos;s wandering at night more. We&apos;re in FAST stage 4 now, and her
                                neurologist adjusted her meds two weeks ago. I&apos;m at 7/10 burnout
                                and carrying this alone, 65 hours a week, isn&apos;t sustainable.
                                I don&apos;t need you to fly out. I need three specific things this month:
                            </p>
                            <ol className="ml-5 space-y-1.5 list-decimal marker:text-teal-dark marker:font-semibold">
                                <li>
                                    <strong>Pharmacy refill calls (3-4 calls):</strong> CVS keeps requiring
                                    prior auth on the new memantine. Can you handle these calls for the
                                    next 30 days? I&apos;ll forward the rx numbers.
                                </li>
                                <li>
                                    <strong>$200 for two nights of respite:</strong> A vetted overnight
                                    sitter so I can sleep one full night next week and one the week
                                    after. Venmo works.
                                </li>
                                <li>
                                    <strong>When you visit in March:</strong> Block a half-day to take
                                    Mom to a notary and finalize her MPOA. It&apos;s drafted; we just
                                    need her signature while she still has capacity.
                                </li>
                            </ol>
                            <p>
                                None of these require living here. All of them help. Tell me by Sunday
                                which you can take.
                            </p>
                            <p>I love you. Sarah.</p>
                        </div>
                    </div>

                    <p className="mt-4 text-xs text-[#4C5551] italic">
                        Generated by Lane from Sarah&apos;s intake (FAST 4, wandering, recent meds, MPOA
                        incomplete, family out of state, burnout 7/10) and her conversation history.
                        Sarah edits and sends.
                    </p>
                </section>

                {/* Footer */}
                <footer className="pt-4 border-t border-[#E2E8F0] text-sm text-[#4C5551]">
                    Memory Lane offers educational and organizational support only. It is not medical advice,
                    diagnosis, treatment, emergency care, or legal advice.
                </footer>
            </main>
        </div>
    );
}
